import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Building2,
  MessageSquare,
  Users,
  Headphones,
  Award,
  Calendar,
  ArrowRight,
  FileText
} from 'lucide-react';
import ContactForm from '../components/ContactForm';
import MeetingScheduler from '../components/MeetingScheduler';

const Contact: React.FC = () => {
  const [isMeetingSchedulerOpen, setIsMeetingSchedulerOpen] = useState(false);

  const handleContactSuccess = (data: any) => {
    console.log('Contact form submitted successfully:', data);
  };

  const handleContactError = (error: string) => {
    console.error('Contact form error:', error);
  };

  const handleScheduleMeeting = () => {
    setIsMeetingSchedulerOpen(true);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 80965 56344'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@crmonce.com'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: ['8-112, Gamallapalem, Kodurupadu, Nellore', 'Andhra Pradesh, India'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Clock,
      title: 'Hours',
      details: ['Mon-Fri: 9:00 AM - 6:00 PM', 'Sat: 10:00 AM - 2:00 PM'],
      color: 'from-orange-500 to-red-500'
    }
  ];



  const officeLocations = [
    {
      city: 'Nellore, Andhra Pradesh',
      country: 'India',
      address: '8-112, Gamallapalem, Kodurupadu',
      phone: '+91  80965 56344',
      email: 'info@crmonce.com'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient particle-bg relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container-custom relative z-10 py-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
             <span className="gradient-text">Get in Touch</span>
            </h1>
            <p className="text-xl text-black/90 max-w-3xl mx-auto mb-8">
              Ready to transform your business? Let's discuss how CRMONCE can help you leverage the full power of Microsoft Dynamics 365 and Power Platform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                <span className="gradient-text">Let's Start a Conversation</span>
            </h2>
            <p className="text-xl text-black-600 max-w-3xl mx-auto">
              We're here to help you succeed. Reach out to us through any of these channels or fill out the form below.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="feature-card text-center"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-2xl`}>
                  <info.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-gray-600">{detail}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-gradient py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <ContactForm
                onSuccess={handleContactSuccess}
                onError={handleContactError}
                title="Send us a Message"
                description="Get in touch with our team and we'll respond within 24 hours."
              />
            </motion.div>

            {/* Office Locations */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-3xl font-bold gradient-text mb-8">Our Office Location</h3>
              <div className="space-y-6">
                {officeLocations.map((office, index) => (
                  <motion.div
                    key={office.city}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="feature-card"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          {office.city}, {office.country}
                        </h4>
                        <div className="space-y-2 text-gray-600">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-primary-500" />
                            <span>{office.address}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-primary-500" />
                            <span>{office.phone}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-primary-500" />
                            <span>{office.email}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 feature-card">
                <h4 className="text-xl font-bold gradient-text mb-4">Schedule a Meeting</h4>
                <p className="text-gray-600 mb-4">
                  Prefer to schedule a call? We're available for virtual meetings and can accommodate your timezone.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleScheduleMeeting}
                  className="w-full px-6 py-3 border-2 border-primary-500 text-primary-600 rounded-lg font-semibold transition-all duration-300 hover:bg-primary-50 hover:border-primary-400 hover:text-primary-700"
                >
                  <Calendar className="mr-2 w-5 h-5" />
                  Schedule a Call
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
             <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get quick answers to common questions about our services and process.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: "What is the typical project timeline?",
                answer: "Project timelines vary based on complexity. Simple implementations take 4-8 weeks, while complex enterprise solutions may take 12-16 weeks. We'll provide a detailed timeline during our initial consultation."
              },
              {
                question: "Do you provide ongoing support?",
                answer: "Yes, we offer comprehensive support packages including maintenance, updates, training, and 24/7 technical support to ensure your solution continues to perform optimally."
              },
              {
                question: "What industries do you specialize in?",
                answer: "We work across various industries including manufacturing, retail, healthcare, financial services, and professional services. Our solutions are tailored to meet industry-specific requirements."
              },
              {
                question: "How do you ensure data security?",
                answer: "We follow Microsoft's security best practices and implement enterprise-grade security measures including encryption, access controls, and regular security audits to protect your data."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="feature-card"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join hundreds of organizations that have already revolutionized their operations with CRMONCE.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-accent text-lg px-8 py-4"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline text-lg px-8 py-4"
              >
                Download Brochure
                <FileText className="ml-2 w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* About Us Link Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Learn More About <span className="gradient-text">CRMONCE</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Discover our story, values, and the team behind CRMONCE's success in Microsoft Dynamics 365 and Power Platform solutions.
              </p>
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary text-lg px-8 py-4"
                >
                  About Our Company
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Meeting Scheduler Modal */}
      <MeetingScheduler
        isOpen={isMeetingSchedulerOpen}
        onClose={() => setIsMeetingSchedulerOpen(false)}
        source="contact_page"
      />
    </div>
  );
};

export default Contact; 